<template>
  <div>
    <v-form ref="inputForm" @submit.prevent="">
      
      <PostalAddress
        :title="$let('HomeDetailsSubSectionTitle')"
        v-model="model.address"
        :countryID="model.countryOfResidenceID"
        :rules="rules"
        propertyModelName="Address">
      </PostalAddress>
          
      <v-row>
        <v-col cols="12" md="6">
          <Combobox
            v-if="elsInUse.CountryOfResidenceID"
            v-model="model.countryOfResidenceID" 
            :items="countriesDS" 
            :label="elsLabel.CountryOfResidenceID"
            :helperText="elsHelperText.CountryOfResidenceID"
            :rules="[elsRequired.CountryOfResidenceID ? rules.required : true]">
          </Combobox>
        </v-col>
        <v-col cols="12" md="6">
          <Combobox
            v-if="elsInUse.AccomodationTypeID"
            v-model="model.accomodationTypeID" 
            :items="accomodationsDS"
            :label="elsLabel.AccomodationTypeID"
            :helperText="elsHelperText.AccomodationTypeID"
            :rules="[elsRequired.AccomodationTypeID ? rules.required : true]">
          </Combobox>          
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <TextField 
            type="number"
            :min="0"
            :max="99"
            v-model="model.yearsAtCurrentAddress" 
            @input="_yearsAtCurrentAddressChange"
            v-if="elsInUse.YearsAtCurrentAddress"
            :label="elsLabel.YearsAtCurrentAddress"
            :helperText="elsHelperText.YearsAtCurrentAddress"
            :rules="[rules.numeric, elsRequired.YearsAtCurrentAddress ? rules.required : true]">
          </TextField>
        </v-col>
        <v-col cols="12" md="6"></v-col>
      </v-row>

      <v-expand-transition>
        <v-sheet v-if="yearsDep">
            
            <div v-html="$let('PreviousHomeDetailsSubSectionTitle')" class="subtitle-enquiry my-5"></div>
            
            <PostalAddress
              v-model="model.prevAddress"
              :countryID="model.prevCountryOfResidenceID"
              :rules="rules"
              propertyModelName="PrevAddress">
            </PostalAddress>
            
            <v-row>
              <v-col cols="12" md="6">
                <Combobox
                  v-if="elsInUse.PrevCountryOfResidenceID"
                  v-model="model.prevCountryOfResidenceID" 
                  :items="countriesDS" 
                  :label="elsLabel.PrevCountryOfResidenceID"
                  :helperText="elsHelperText.PrevCountryOfResidenceID"
                  :rules="[elsRequired.PrevCountryOfResidenceID ? rules.required : true]">
                </Combobox>
              </v-col>
              <v-col cols="12" md="6"></v-col>
            </v-row>
        </v-sheet>
      </v-expand-transition>

      <div v-html="$let('FamilyDetailsSubSectionTitle')" class="subtitle-enquiry my-5"></div>

      <v-row>
        
        <v-col cols="12" md="12"> 
          <v-row  class="FamilyDetailsSubSectionRow">
            <v-col cols="12" md="6">
              <Combobox
                v-if="elsInUse.MaritalStatusID"
                v-model="model.maritalStatusID" 
                :items="maritalStatusDS" 
                :label="elsLabel.MaritalStatusID"
                :helperText="elsHelperText.MaritalStatusID"
                :rules="[elsRequired.MaritalStatusID ? rules.required : true]">
              </Combobox>
            </v-col>

            <v-col cols="12" md="6">
              <TextField 
                type="number"
                :min="0"
                :max="10"
                v-model="model.numberOfDependants" 
                v-if="elsInUse.NumberOfDependants"
                :label="elsLabel.NumberOfDependants"
                :helperText="elsHelperText.NumberOfDependants"
                :rules="[elsRequired.NumberOfDependants ? rules.required : true]">
              </TextField>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12" md="12" >
          <v-row>
            <v-col cols="12" md="6">
            </v-col>
            <v-col cols="12" md="6" class="DateOfBirthDependant">
              <v-sheet v-if="model.numberOfDependants">
                <!-- DependantsDateOfBirth will always be required if numberOfDependants is InUse -->
                <DatePicker v-for="n in parseInt(model.numberOfDependants)" :key="n"
                  v-model="model.dependantsDateOfBirth[n-1]"
                  :label="$let('DateOfBirthDependentsLabel').replace('{n}', n)" 
                  :useIcon="true"
                  :birthdayMode="true"
                  :helperText="elsHelperText.dependantsDateOfBirth"
                  :max="generic.todayString()"
                  :min="(new Date()).dateAdd(-100)"
                  :rules="[rules.required]">
                </DatePicker>
              </v-sheet>
            </v-col>
          </v-row>
        </v-col>


      </v-row>

      <StepperButtons 
        @continueClick="_stepperContinue"
        @backClick="_stepperBack">
      </StepperButtons>
    </v-form>
  </div>
</template>

<script>

  import { mapGetters } from "vuex";
  import TextField from '@/shared/components/text-field.vue';
  import Combobox from '@/shared/components/combobox.vue';
  import DatePicker from '@/shared/components/date-picker.vue';
  import PostalAddress from './postal-address.vue';
  import StepperButtons from './stepper-buttons.vue';
  import opmGeneric from '../utils/generic.js';
  import generic from '@/shared/utils/generic.js';
  import paramKeys from '../constants/param-keys.js';

  export default {
    name: 'HouseholdDetails',
    components: { 
      PostalAddress,
      StepperButtons, 
      TextField, 
      DatePicker,
      Combobox
    },
    props: {
      rules: { type: Object },
    },
    emits: ["continueClick", "backClick"],
    data: () => ({
      generic,
      countriesDS: [],
      accomodationsDS: [],
      maritalStatusDS: [],
      dependantsDS: [],
      yearsDep: false,
      model: {
        address: undefined,
        countryOfResidenceID: undefined,
        accomodationTypeID: undefined,
        yearsAtCurrentAddress: undefined,
        prevAddress: undefined,
        prevCountryOfResidenceID: undefined,
        maritalStatusID: undefined,
        numberOfDependants: undefined,
        dependantsDateOfBirth: []
      }
    }),
    async created() { 
      opmGeneric.setElementsConfig(this, this.elementsConfig); 
      // DropdownLists sources
      this.countriesDS = this.referenceData ? this.referenceData.Countries : [];
      this.accomodationsDS = this.referenceData ? this.referenceData.AccomodationTypes : [];
      this.maritalStatusDS = this.referenceData ? this.referenceData.MaritalStatus : [];
      
    },
    methods: {
      _stepperContinue() { 
        this.$emit("continueClick", this.model, this.$refs.inputForm); 
      },
      
      _stepperBack() { 
        this.$emit("backClick"); 
      },

      _yearsAtCurrentAddressChange(value) {
        if (!value) this.yearsDep = false;
        else this.toggleYearsDependencies(value);
      },

      toggleYearsDependencies(years) {
        this.yearsDep = parseInt(years) <= parseInt(generic.paramValue(this.$store, paramKeys.PreviousAddressRequiredInYears));
      }
    },
    computed: {
      ...mapGetters(["referenceData", "elementsConfig"]),
    }
  }
</script>